import { Link } from "@/lib/router-compat";
import Layout from "@/components/layout/Layout";
import { renderUrl } from "@/lib/imageUtils";
import { rvPlaceholder } from "@/lib/rvPlaceholders";
import type { CategoryConfig, LandingUnit } from "@/lib/categoryLanding";

function money(n: number | null) {
  return n ? `$${Math.round(n).toLocaleString()}` : null;
}

export default function CategoryLanding({ cfg, units, unavailable }: { cfg: CategoryConfig; units: LandingUnit[]; unavailable: boolean }) {
  return (
    <Layout>
      <section className="container py-8 md:py-12">
        <nav aria-label="Breadcrumb" className="mb-3 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link> / <Link to="/rv-search" className="hover:underline">RVs for Sale</Link> / <span>{cfg.h1}</span>
        </nav>
        <h1 className="font-heading text-3xl font-bold uppercase tracking-wide text-foreground md:text-4xl">{cfg.h1}</h1>
        <p className="mt-3 max-w-3xl text-base text-muted-foreground md:text-lg">{cfg.intro}</p>

        {units.length > 0 ? (
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {units.map((u, i) => {
              const price = money(u.sale_price || u.price);
              const was = u.sale_price && u.price && u.sale_price < u.price ? money(u.price) : null;
              const img = u.image ? renderUrl(u.image, 480, 60) : rvPlaceholder(u.rv_type);
              return (
                <li key={u.slug} className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
                  <Link to={`/rv-search/${u.slug}`} className="block">
                    <div className="aspect-[4/3] bg-muted">
                      <img src={img} alt={u.title} width={480} height={360} loading={i < 4 ? "eager" : "lazy"} decoding="async" className="h-full w-full object-cover" />
                    </div>
                    <div className="p-4">
                      <p className="text-xs uppercase text-muted-foreground">
                        {[u.condition, u.rv_type, u.status === "coming_soon" ? "Coming Soon" : null].filter(Boolean).join(" · ")}
                      </p>
                      <h2 className="mt-1 line-clamp-2 font-semibold text-card-foreground">{u.title}</h2>
                      <p className="mt-2 text-lg font-bold text-primary">
                        {price ?? "Call for price"}
                        {was && <span className="ml-2 text-sm font-normal text-muted-foreground line-through">{was}</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">Stock #{u.stock_number}</p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="mt-8 rounded-lg border border-border bg-muted/40 p-6">
            <p className="text-foreground">
              {unavailable ? "Inventory is loading slowly right now." : "No units in this category are on the lot right now."}{" "}
              Browse our full inventory or a related category below.
            </p>
          </div>
        )}

        <div className="mt-8">
          <a href={cfg.searchHref} className="inline-flex rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground hover:bg-primary/90">
            Filter &amp; sort all {cfg.h1.replace(/ for Sale$/, "")}
          </a>
        </div>

        <section className="mt-10" aria-labelledby="related-cats">
          <h2 id="related-cats" className="font-heading text-xl font-semibold uppercase text-foreground">Related categories</h2>
          <ul className="mt-3 flex flex-wrap gap-3">
            {cfg.related.map((r) => (
              <li key={r.href}>
                <Link to={r.href} className="inline-flex rounded-full border border-border px-4 py-2 text-sm text-foreground hover:bg-muted">{r.label}</Link>
              </li>
            ))}
          </ul>
        </section>
      </section>
    </Layout>
  );
}
